import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import ApproveIcon from "@styles/all-images/icons/checkblue.svg";
import RejectIcon from "@styles/all-images/icons/closeblue.svg";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import dateHandler from "@/composables/dateUtil";
import plusblue from "@styles/all-images/icons/plusblue.svg";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IMTSDocumentApprovalStatus } from "@/constants/enums/im-ts-document-status.enum";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import TierConfigurationRejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";

interface IMDocumentTableT1T2Props {
  tabId: number;
}

const IMDocumentTableT1T2: React.FC<IMDocumentTableT1T2Props> = ({ tabId }) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDateWithTime } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [docRejectingID, setDocRejectingID] = useState(null);
  const [showCreateTSConfirm, setShowCreateTSConfirm] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const { isPermitted } = usePermissionState();

  const handleView = useCallback((doc: any) => {
    navigate(`/view-im-ts-document/${doc.id}?type=im&history=false`);
  }, []);

  const handleApprove = useCallback(
    async (doc: any) => {
      try {
        navigate(`/view-im-ts-document/${doc.id}?type=im&history=false`);
      } catch (error) {
        console.error("Error approving document:", error);
      }
    },
    [dispatch, navigate],
  );

  const toggleRejectModal = (doc?: any) => {
    setIsRejectModalOpen(!isRejectModalOpen);
    if (doc) {
      setDocRejectingID(doc.id);
    }
  };

  const handleReject = useCallback(
    async (note: string) => {
      if (!docRejectingID) {
        return;
      }
      setIsLoading(true);
      try {
        const { authenticatedCall } = actionsHandler(
          "approveRejectIMTSDocument",
        );
        const payload = {
          status: IMTSDocumentApprovalStatus.Rejected,
          note,
        };
        const response = await dispatch(
          authenticatedCall({
            method: "put",
            customParams: [docRejectingID],
            payload,
          }),
        );
        toast.success(response.data.message);
        setForceRefresh(true);
        toggleRejectModal();
        setDocRejectingID(null);
      } catch (error) {
        console.error("Error rejecting document:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, docRejectingID],
  );

  const handleCreateTsDocument = useCallback((doc: any) => {
    setSelectedDoc(doc);
    setShowCreateTSConfirm(true);
  }, []);

  const confirmCreateTS = () => {
    navigate(`/document/im-document/create-ts-document/${selectedDoc.id}/`);
    setShowCreateTSConfirm(false);
  };

  const columns = [
    {
      header: "Document Name",
      accessor: (row: any) => row.document_name || NO_DATA_AVAILABLE,
      sortable: true,
      sortKey: "document_name",
    },
    {
      header: "Received on Date & Time",
      accessor: (row: any) => formatDateWithTime(row.received_on),
      sortable: true,
      sortKey: "received_on",
    },
    {
      header: "Client Name(T4)",
      accessor: "client_name",
      sortable: true,
      sortKey: "client_name",
    },
    {
      header: "Sent By",
      accessor: "sent_by",
      sortable: true,
      sortKey: "sent_by",
    },
    {
      header: "Approval Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.approval_status}
          type={StatusChipEnumType.IMTSDocumentApproval}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: false,
    },
    {
      header: "Actions",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>

          {row.approval_status === IMTSDocumentApprovalStatus.Pending &&
            isPermitted(DocumentsPermissions.APPROVE_REJECT_IM_DOCUMENTS) && (
              <div className="tw-flex tw-items-center">
                <button onClick={() => handleApprove(row)} title="Approve">
                  <img
                    src={ApproveIcon}
                    alt="Approve"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
                <button onClick={() => toggleRejectModal(row)} title="Reject">
                  <img
                    src={RejectIcon}
                    alt="Reject"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              </div>
            )}
          {row.approval_status === IMTSDocumentApprovalStatus.Approved &&
            !row.is_term_sheet_created &&
            isPermitted(DocumentsPermissions.CREATE_TERMS_SHEET_DOCUMENTS) && (
              <button
                onClick={() => handleCreateTsDocument(row)}
                title="Create T4 Document"
              >
                <img
                  src={plusblue}
                  alt="Plus"
                  className="tw-w-8 tw-h-8 tw-max-w-8 tw-max-h-8 tw-min-w-8 tw-min-h-8"
                />
              </button>
            )}
        </div>
      ),
    },
  ];

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("imDocumentListForFunder");

        const queryParams = new URLSearchParams({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          id: tabId.toString(),
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryParams,
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching IM documents:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1, limit: 10 },
          },
        };
      }
    },
    [dispatch, tabId],
  );

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        noDataMessage="No documents found"
        noSearchDataMessage="No matching documents found"
      />

      {isRejectModalOpen && (
        <TierConfigurationRejectionReason
          onClose={toggleRejectModal}
          onSubmit={handleReject}
          isLoading={isLoading}
        />
      )}

      {showCreateTSConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">
                Confirm Create Terms Sheet
              </h2>
              <button onClick={() => setShowCreateTSConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to create a Terms Sheet for this client?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowCreateTSConfirm(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmCreateTS}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IMDocumentTableT1T2;
